/**
 * Provider component that handles CEISA WebSocket connection and displays notifications
 *
 * Usage: Wrap your dashboard layout with this provider
 *
 * Example:
 * ```tsx
 * <CeisaNotificationProvider ppjkNpwp={user.ppjkNpwp}>
 *   <YourDashboardContent />
 * </CeisaNotificationProvider>
 * ```
 */
import React, { useCallback, useMemo } from 'react';
import { useCeisaWebSocket } from '@hooks/useCeisaWebSocket';
import { useApprovalWebSocket } from '@hooks/approval/use-approval-websocket';
import { CeisaNotificationContainer } from './CeisaNotification';
import { usePpjkRealtimeInvalidation } from '@/features/realtime/ppjkEvents';

interface CeisaNotificationProviderProps {
  ppjkNpwp?: string;
  children: React.ReactNode;
}

export const CeisaNotificationProvider: React.FC<CeisaNotificationProviderProps> = ({ ppjkNpwp, children }) => {
  const { visualNotifications: ceisaNotifs, removeVisualNotification: removeCeisa } = useCeisaWebSocket(ppjkNpwp);
  // Consignee reads stay current when anyone in the PPJK changes the master.
  usePpjkRealtimeInvalidation();
  const { visualNotifications: approvalNotifs, removeVisualNotification: removeApproval } =
    useApprovalWebSocket(ppjkNpwp);

  // Merge both notification streams
  const allNotifications = useMemo(() => [...ceisaNotifs, ...approvalNotifs], [ceisaNotifs, approvalNotifs]);

  const handleRemove = useCallback(
    (id: string) => {
      removeCeisa(id);
      removeApproval(id);
    },
    [removeCeisa, removeApproval]
  );

  // Log WebSocket status
  React.useEffect(() => {
    if (!ppjkNpwp) {
      console.warn(
        '⚠️ CeisaNotificationProvider: No PPJK NPWP provided. WebSocket notifications will not work. User needs PPJK NPWP in their profile.'
      );
    }
  }, [ppjkNpwp]);

  return (
    <>
      {children}

      {/* Notification container — renders both CEISA and approval popups */}
      <CeisaNotificationContainer notifications={allNotifications} onRemove={handleRemove} />
    </>
  );
};

export default CeisaNotificationProvider;
